import { useEffect, useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import {
  ArrowRight,
  CheckCircle2,
  LayoutGrid,
  ShieldCheck,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";

const BRAND = "Purpleflares.com";

const translations = {
  en: {
    nav: {
      cta: "Get started",
    },
    hero: {
      badge: "Mobile Hub",
      title: "Discover fantastic mobile games and apps",
      subtitle:
        "Explore the best mobile games and essential apps in one sleek, fast hub—ready whenever you are.",
      primaryCta: "Explore now",
      secondaryCta: "Learn more",
      imageAlt: "Abstract mobile hub illustration",
      pills: {
        fast: "Fast",
        verified: "Verified",
        topPicks: "Top picks",
      },
    },
    why: {
      title: "Why choose our mobile hub?",
      subtitle:
        "Experience the best mobile games and apps in one convenient place.",
    },
    features: {
      wideChoice: {
        title: "Wide selection",
        desc: "Browse hundreds of carefully curated mobile games and must‑have apps for your device.",
      },
      fastAccess: {
        title: "Fast access",
        desc: "Optimized performance and secure delivery—seconds, not minutes.",
      },
      topRated: {
        title: "Highly rated content",
        desc: "Discover top‑rated games and apps loved by millions worldwide.",
      },
      safe: {
        title: "Safe & reliable",
        desc: "Verified content with a focus on keeping your device protected.",
      },
    },
    how: {
      title: "How it works",
      steps: [
        {
          title: "Browse",
          desc: "Find games and apps that match your vibe.",
        },
        {
          title: "Pick your favorites",
          desc: "Open details and choose what you want to try next.",
        },
        {
          title: "Enjoy instantly",
          desc: "Jump in fast and keep discovering.",
        },
      ],
    },
    discover: {
      title: "Made for discovering the best apps",
      subtitle:
        "From quick games to everyday essentials—keep it simple, fast, and fun.",
      imageAlt: "Catalog preview illustration",
      chips: [
        "Arcade",
        "Puzzle",
        "Action",
        "Racing",
        "Kids",
        "Productivity",
        "Utilities",
        "Entertainment",
      ],
    },
    security: {
      imageAlt: "Security illustration",
    },
    final: {
      title: "Ready to discover your next favorite?",
      subtitle: "Open the hub and start exploring in seconds.",
      cta: "Get it now",
    },
    footer: {
      legal: "Legal",
      imprint: "Imprint",
      contact: "Contact",
      privacy: "Privacy",
      terms: "Terms",
      withdrawal: "Withdrawal",
      start: "Start",
      rights: "All rights reserved.",
    },
    lang: {
      label: "Language",
      en: "English",
      de: "Deutsch",
    },
  },
  de: {
    nav: {
      cta: "Jetzt starten",
    },
    hero: {
      badge: "Mobile Hub",
      title: "Entdecke fantastische mobile Games und Apps",
      subtitle:
        "Finde die besten mobilen Games und nützliche Apps an einem Ort – elegant, schnell und jederzeit bereit.",
      primaryCta: "Jetzt entdecken",
      secondaryCta: "Mehr erfahren",
      imageAlt: "Abstrakte Illustration eines Mobile Hubs",
      pills: {
        fast: "Schnell",
        verified: "Verifiziert",
        topPicks: "Top-Auswahl",
      },
    },
    why: {
      title: "Warum unseren Mobile Hub wählen?",
      subtitle:
        "Erlebe die besten mobilen Games und Apps an einem praktischen Ort.",
    },
    features: {
      wideChoice: {
        title: "Große Auswahl",
        desc: "Stöbere durch hunderte sorgfältig ausgewählte Mobile Games und unverzichtbare Apps für dein Gerät.",
      },
      fastAccess: {
        title: "Schneller Zugriff",
        desc: "Optimierte Geschwindigkeit und sichere Bereitstellung – in Sekunden, nicht in Minuten.",
      },
      topRated: {
        title: "Hoch bewertete Inhalte",
        desc: "Entdecke top bewertete Games und Apps, die weltweit von Millionen geliebt werden.",
      },
      safe: {
        title: "Sicher & zuverlässig",
        desc: "Verifizierte Inhalte mit Fokus auf den Schutz deines Geräts.",
      },
    },
    how: {
      title: "So funktioniert’s",
      steps: [
        {
          title: "Stöbern",
          desc: "Finde Games und Apps, die zu dir passen.",
        },
        {
          title: "Favoriten wählen",
          desc: "Details ansehen und auswählen, was du als Nächstes testen möchtest.",
        },
        {
          title: "Sofort loslegen",
          desc: "Schnell starten und weiter entdecken.",
        },
      ],
    },
    discover: {
      title: "Für die besten Apps gemacht",
      subtitle:
        "Von schnellen Games bis zu praktischen Tools – einfach, schnell und mit Spaß entdecken.",
      imageAlt: "Illustration einer Katalogvorschau",
      chips: [
        "Arcade",
        "Puzzle",
        "Action",
        "Rennen",
        "Kinder",
        "Produktivität",
        "Tools",
        "Entertainment",
      ],
    },
    security: {
      imageAlt: "Sicherheitsillustration",
    },
    final: {
      title: "Bereit für deinen nächsten Favoriten?",
      subtitle: "Öffne den Hub und starte in Sekunden.",
      cta: "Jetzt loslegen",
    },
    footer: {
      legal: "Rechtliches",
      imprint: "Impressum",
      contact: "Kontakt",
      privacy: "Datenschutz",
      terms: "AGB",
      withdrawal: "Widerrufsrecht",
      start: "Start",
      rights: "Alle Rechte vorbehalten.",
    },
    lang: {
      label: "Sprache",
      en: "English",
      de: "Deutsch",
    },
  },
};

function pickInitialLang() {
  const nav = (typeof navigator !== "undefined" && navigator.language) || "en";
  return nav.toLowerCase().startsWith("de") ? "de" : "en";
}

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function PrimaryButton({ href, children }) {
  return (
    <a
      href={href}
      className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-sm ring-1 ring-purple-600/20 transition hover:from-violet-700 hover:to-purple-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-purple-300"
    >
      {children} <ArrowRight className="h-4 w-4" />
    </a>
  );
}

function SecondaryButton({ href, children }) {
  return (
    <a
      href={href}
      className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-purple-200"
    >
      {children}
    </a>
  );
}

function SmartImage({ src, alt, className, loading }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={classNames(
          "relative grid place-items-center overflow-hidden bg-gradient-to-br from-purple-50 to-violet-50",
          className
        )}
      >
        <div className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-purple-200/40 blur-2xl" />
        <div className="absolute -bottom-20 -right-16 h-56 w-56 rounded-full bg-violet-200/40 blur-2xl" />
        <div className="relative inline-flex items-center gap-2 rounded-xl bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 backdrop-blur">
          <Sparkles className="h-4 w-4 text-purple-700" />
          Image placeholder
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      onError={() => setFailed(true)}
    />
  );
}

function LangSwitch({ lang, setLang, t }) {
  return (
    <div className="flex items-center gap-1 rounded-xl bg-white/80 p-1 shadow-sm ring-1 ring-slate-200 backdrop-blur">
      <button
        type="button"
        onClick={() => setLang("en")}
        aria-label={`${t.lang.label}: ${t.lang.en}`}
        className={classNames(
          "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition",
          lang === "en"
            ? "bg-purple-600 text-white shadow-sm"
            : "text-slate-700 hover:bg-slate-50"
        )}
      >
        <ReactCountryFlag
          countryCode="GB"
          svg
          style={{ width: "1.25em", height: "1.25em" }}
          aria-label="English"
        />
        <span className="hidden sm:inline">{t.lang.en}</span>
      </button>

      <button
        type="button"
        onClick={() => setLang("de")}
        aria-label={`${t.lang.label}: ${t.lang.de}`}
        className={classNames(
          "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition",
          lang === "de"
            ? "bg-purple-600 text-white shadow-sm"
            : "text-slate-700 hover:bg-slate-50"
        )}
      >
        <ReactCountryFlag
          countryCode="DE"
          svg
          style={{ width: "1.25em", height: "1.25em" }}
          aria-label="Deutsch"
        />
        <span className="hidden sm:inline">{t.lang.de}</span>
      </button>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center gap-3">
        <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50 text-purple-700 ring-1 ring-purple-100">
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-base font-semibold text-slate-900">{title}</div>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-600">{desc}</p>
    </div>
  );
}

function Step({ index, title, desc }) {
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center gap-3">
        <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-sm font-bold text-white">
          {index}
        </div>
        <div className="text-base font-semibold text-slate-900">{title}</div>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-600">{desc}</p>
    </div>
  );
}

export default function App() {
  const [lang, setLang] = useState(pickInitialLang);
  const t = useMemo(() => translations[lang] ?? translations.en, [lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <div className="min-h-screen bg-white">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 left-1/2 h-72 w-[48rem] -translate-x-1/2 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="absolute -bottom-24 right-1/3 h-72 w-[44rem] rounded-full bg-violet-200/40 blur-3xl" />
      </div>

      <header className="sticky top-0 z-20 border-b border-slate-200/60 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <a href="/" className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 text-white shadow-sm">
              <Sparkles className="h-5 w-5" />
            </span>
            <span className="text-sm font-extrabold tracking-tight text-slate-900">
              {BRAND}
            </span>
          </a>

          <div className="flex items-center gap-3">
            <LangSwitch lang={lang} setLang={setLang} t={t} />
            <a
              href="/s"
              className="hidden sm:inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-purple-200"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-6xl px-4 pb-10 pt-14 sm:px-6 sm:pb-16 sm:pt-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-700 ring-1 ring-purple-100">
                <CheckCircle2 className="h-4 w-4" />
                {t.hero.badge}
              </div>

              <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                {t.hero.title}
              </h1>
              <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">
                {t.hero.subtitle}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <PrimaryButton href="/s">{t.hero.primaryCta}</PrimaryButton>
                <SecondaryButton href="/s">{t.hero.secondaryCta}</SecondaryButton>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
                <div className="inline-flex items-center gap-2">
                  <Zap className="h-4 w-4 text-purple-700" />
                  <span>{t.hero.pills.fast}</span>
                </div>
                <div className="inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-purple-700" />
                  <span>{t.hero.pills.verified}</span>
                </div>
                <div className="inline-flex items-center gap-2">
                  <Star className="h-4 w-4 text-purple-700" />
                  <span>{t.hero.pills.topPicks}</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-purple-100 to-violet-50 blur-xl" />
              <div className="relative overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                <SmartImage
                  src="/images/hero.jpeg"
                  alt={t.hero.imageAlt}
                  className="h-auto w-full"
                />
              </div>
              <div className="mt-4 flex items-center justify-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-2 ring-1 ring-slate-200">
                  <ShieldCheck className="h-4 w-4 text-purple-700" />
                  Secure access
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-2 ring-1 ring-slate-200">
                  <Zap className="h-4 w-4 text-purple-700" />
                  Optimized speed
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              {t.why.title}
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              {t.why.subtitle}
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              icon={LayoutGrid}
              title={t.features.wideChoice.title}
              desc={t.features.wideChoice.desc}
            />
            <FeatureCard
              icon={Zap}
              title={t.features.fastAccess.title}
              desc={t.features.fastAccess.desc}
            />
            <FeatureCard
              icon={Star}
              title={t.features.topRated.title}
              desc={t.features.topRated.desc}
            />
            <FeatureCard
              icon={ShieldCheck}
              title={t.features.safe.title}
              desc={t.features.safe.desc}
            />
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-violet-100 to-purple-50 blur-xl" />
              <div className="relative overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                <SmartImage
                  src="/images/catalog.jpeg"
                  alt={t.discover.imageAlt}
                  className="h-auto w-full"
                  loading="lazy"
                />
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                {t.discover.title}
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                {t.discover.subtitle}
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {t.discover.chips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 ring-1 ring-purple-100"
                  >
                    {chip}
                  </span>
                ))}
              </div>

              <div className="mt-8">
                <PrimaryButton href="/s">{t.hero.primaryCta}</PrimaryButton>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                {t.how.title}
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                {t.why.subtitle}
              </p>

              <div className="mt-8 grid gap-5">
                {t.how.steps.map((s, idx) => (
                  <Step
                    key={s.title}
                    index={idx + 1}
                    title={s.title}
                    desc={s.desc}
                  />
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-purple-100 to-violet-50 blur-xl" />
              <div className="relative overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                <SmartImage
                  src="/images/security.jpeg"
                  alt={t.security.imageAlt}
                  className="h-auto w-full"
                  loading="lazy"
                />
              </div>
              <div className="mt-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-purple-700" />
                  <div className="text-sm font-semibold text-slate-900">
                    {t.features.safe.title}
                  </div>
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {t.features.safe.desc}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 pb-16 pt-6 sm:px-6 sm:pb-24">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-12 text-white shadow-sm sm:px-10">
            <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/15 blur-2xl" />
            <div className="relative grid items-center gap-8 lg:grid-cols-2">
              <div>
                <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                  {t.final.title}
                </h2>
                <p className="mt-3 text-base leading-7 text-white/90">
                  {t.final.subtitle}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <a
                  href="/s"
                  className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40"
                >
                  {t.final.cta} <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-sm font-extrabold tracking-tight text-slate-900">
                {BRAND}
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {t.hero.subtitle}
              </p>
            </div>

            <div>
              <div className="text-sm font-semibold text-slate-900">
                {t.footer.legal}
              </div>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li>
                  <a className="hover:text-slate-900" href="/impressum.html">
                    {t.footer.imprint}
                  </a>
                </li>
                <li>
                  <a className="hover:text-slate-900" href="/kontakt.html">
                    {t.footer.contact}
                  </a>
                </li>
                <li>
                  <a className="hover:text-slate-900" href="/privacy.html">
                    {t.footer.privacy}
                  </a>
                </li>
                <li>
                  <a className="hover:text-slate-900" href="/terms.html">
                    {t.footer.terms}
                  </a>
                </li>
                <li>
                  <a className="hover:text-slate-900" href="/widerrufsrecht.html">
                    {t.footer.withdrawal}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <div className="text-sm font-semibold text-slate-900">
                {t.footer.start}
              </div>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li>
                  <a className="hover:text-slate-900" href="/s">
                    {t.nav.cta}
                  </a>
                </li>
              </ul>
            </div>

            <div className="text-sm text-slate-600">
              <div className="text-sm font-semibold text-slate-900">
                {t.lang.label}
              </div>
              <div className="mt-3">
                <LangSwitch lang={lang} setLang={setLang} t={t} />
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-2 border-t border-slate-200 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <div>
              © {new Date().getFullYear()} {BRAND}. {t.footer.rights}
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-700" />
                Sleek • White • Purple
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


